/**
 * Web auth adapter.
 *
 * The real auth token lives in an httpOnly cookie (set by the server after Cognito PKCE callback)
 * and is never accessible to JS. To satisfy AuthContext's expectation of an idToken string with
 * a decodable JWT payload, we build a minimal display-only JWT from the /api/auth/me response.
 * This "display JWT" is never used for actual HTTP auth — that's always the httpOnly cookie.
 */

type AuthSuccessData = { idToken: string; expiresIn: number };

const authSuccessCallbacks: ((data: AuthSuccessData) => void)[] = [];
const authFailureCallbacks: (() => void)[] = [];
const authRefreshedCallbacks: ((data: AuthSuccessData) => void)[] = [];
const loginWindowClosedCallbacks: (() => void)[] = [];

/** Build a display-only JWT that AuthContext can decode for email/role/groups. */
function buildDisplayJwt(info: {
  sub: string;
  email?: string;
  groups: string[];
  exp: number;
}): string {
  const encode = (obj: unknown) =>
    btoa(JSON.stringify(obj)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  const header = encode({ alg: 'none', typ: 'JWT' });
  const payload = encode({
    sub: info.sub,
    email: info.email ?? '',
    'cognito:groups': info.groups,
    exp: info.exp,
    preferred_username: info.email?.split('@')[0] ?? '',
  });
  return `${header}.${payload}.`;
}

/** Check /api/auth/me and fire onAuthSuccess if the session cookie is valid. */
export async function checkWebAuthState(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (!res.ok) return false;
    const info = (await res.json()) as {
      sub: string;
      email?: string;
      groups: string[];
      exp: number;
    };
    const idToken = buildDisplayJwt(info);
    const expiresIn = Math.max(0, info.exp - Math.floor(Date.now() / 1000));
    for (const cb of authSuccessCallbacks) cb({ idToken, expiresIn });
    return true;
  } catch {
    return false;
  }
}

export function createWebAuthApi() {
  return {
    loginWithCognito(): void {
      window.location.href = '/api/auth/login';
    },

    onAuthSuccess(callback: (data: AuthSuccessData) => void): void {
      authSuccessCallbacks.length = 0;
      authSuccessCallbacks.push(callback);
      // Immediately check if already authenticated (page load after callback redirect)
      checkWebAuthState().catch(() => {});
    },

    onAuthFailure(callback: () => void): void {
      authFailureCallbacks.length = 0;
      authFailureCallbacks.push(callback);
    },

    onAuthRefreshed(callback: (data: AuthSuccessData) => void): void {
      authRefreshedCallbacks.length = 0;
      authRefreshedCallbacks.push(callback);
    },

    onLoginWindowClosed(callback: () => void): void {
      loginWindowClosedCallbacks.length = 0;
      loginWindowClosedCallbacks.push(callback);
    },
  };
}

/** Call when the server returns 401 to force re-login. */
export function triggerWebAuthFailure(): void {
  for (const cb of authFailureCallbacks) cb();
}

/** Attempt a silent token refresh via the server. */
export async function silentWebRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    if (!res.ok) {
      triggerWebAuthFailure();
      return false;
    }
    const refreshed = await checkWebAuthState();
    if (refreshed) {
      // also fire onAuthRefreshed
      const meRes = await fetch('/api/auth/me', { credentials: 'include' });
      if (meRes.ok) {
        const info = (await meRes.json()) as {
          sub: string;
          email?: string;
          groups: string[];
          exp: number;
        };
        const idToken = buildDisplayJwt(info);
        const expiresIn = Math.max(0, info.exp - Math.floor(Date.now() / 1000));
        for (const cb of authRefreshedCallbacks) cb({ idToken, expiresIn });
      }
    }
    return refreshed;
  } catch {
    return false;
  }
}
